import { addYears, format, isValid, parse } from 'date-fns';

export const currentYear = new Date().getFullYear() % 100;
export const nextYear = addYears(new Date(), 1).getFullYear() % 100;

/** Current financial year folder, e.g. `26-27` when the calendar year is 2026. */
export const getCurrentYearFolder = () => `${currentYear}-${nextYear}`;

/**
 * Maps a date to a year folder string (e.g. 2027 → `27-28`).
 * Returns an empty string when the date is missing or invalid.
 */
export const getYearFolderFormat = (dateValue) => {
	const date = safelyParseDate(dateValue);
	if (!date) return '';

	const year = date.getFullYear();
	const shortYear = String(year).slice(-2);
	const nextShortYear = String(year + 1).slice(-2);
	return `${shortYear}-${nextShortYear}`;
};

export const DATE_SORT_FIELDS = new Set([
	'createDate',
	'createdDate',
	'createdWhen',
	'dateCreated',
	'estimatedCloseDate',
	'renewalDate',
	'dateOfLoss',
	'dateClosed',
	'effectiveDate',
	'nextTaskDate',
	'dueDate',
	'lastUpdated',
	'lastContactedAt',
	'nextActionDate',
	'timestamp',
]);

/**
 * Converts any supported date input into yyyy-MM-dd string for Insight (birth date with no time).
 *
 * Supported:
 *  - Date
 *  - Firebase Timestamp
 *  - milliseconds / unix number
 *  - yyyy-MM-dd
 *  - yyyy/MM/dd
 *  - dd/MM/yyyy
 *  - dd-MM-yyyy
 *  - ISO strings
 *
 * Returns null if invalid.
 */
export const parseToInsightDateString = (value) => {
	const date = safelyParseDate(value);
	if (!date) return null;
	return format(date, 'yyyy-MM-dd');
};

export const safelyParseDate = (value) => {
	if (!value) return null;

	let date;

	// Firebase Timestamp
	if (value?.toDate instanceof Function) {
		date = value.toDate();
	} else if (value?.seconds) {
		date = new Date(value.seconds * 1000);
	} else if (value?._seconds) {
		date = new Date(value._seconds * 1000);
	}

	// JS Date
	else if (value instanceof Date) {
		date = value;
	}

	// ISO-capable object
	else if (value?.toISOString instanceof Function) {
		date = new Date(value);
	}

	// milliseconds / unix timestamp
	else if (typeof value === 'number') {
		const timestamp =
			value < 1000000000000
				? value * 1000 // seconds
				: value; // milliseconds

		date = new Date(timestamp);
	}

	// String inputs
	else if (typeof value === 'string') {
		const trimmed = value.trim();

		const formats = [
			'yyyy/MM/dd',
			'yyyy-MM-dd',
			'dd/MM/yyyy',
			'dd-MM-yyyy',
			'MM/dd/yyyy',
			'MM-dd-yyyy',
		];

		for (const fmt of formats) {
			const parsed = parse(trimmed, fmt, new Date());
			if (isValid(parsed)) {
				date = parsed;
				break;
			}
		}

		if (!date) {
			const isoDateOnly = trimmed.match(
				/^\d{4}-\d{2}-\d{2}(?:[T ]00:00(?::00(?:\.0+)?){0,1}(?:Z|[+-]\d{2}:?\d{2})?)?$/,
			);
			if (isoDateOnly) {
				const [year, month, day] = trimmed.split('T')[0].split('-');
				date = new Date(Number(year), Number(month) - 1, Number(day));
			}
		}

		// fallback → native ISO parsing
		if (!date) {
			const native = new Date(trimmed);
			if (isValid(native)) {
				date = native;
			}
		}
	}
	if (!date || !isValid(date)) return null;

	return date;
};

const dateFormats = [/^\d{4}(?:-|\/)\d{2}(?:-|\/)\d{2}/, /^\d{2}(?:-|\/)\d{2}(?:-|\/)\d{4}/];

const isDateLike = (v) =>
	v instanceof Date ||
	(typeof v === 'string' && dateFormats.some((r) => r.test(v))) ||
	v?.toDate instanceof Function;

export const normalize = (value) => {
	if (isDateLike(value)) {
		return safelyParseDate(value);
	}
	return value;
};

export const extractMidnightUTCTimestamp = (dateValue) => {
	// Get the local time zone offset in minutes
	const timezoneOffset = dateValue.getTimezoneOffset();
	// Adjust the date to UTC by adding the time zone offset
	dateValue.setMinutes(dateValue.getMinutes() - timezoneOffset);
	// Set the time to midnight UTC
	return dateValue.setUTCHours(0, 0, 0, 0);
};
